// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; captions label the adjacent control (same convention as the other admin screens).
//
// =============================================================================
// SORulesAdmin — Manage → Unleashed → SO Rules
// =============================================================================
// Admin/SuperUser editor for `so_rules` (db-69): the ordered pipeline the Sales
// Order engine reads at Confirm & Send (docs/so-rules-engine.md). Shows the rules
// in priority order with an add/edit modal (all selector + output fields) and an
// activate/deactivate toggle. The engine reads this table live — editing a rule
// changes SO routing with no deploy.
// =============================================================================
import React from "react";
import { getConfigValues, upsertConfig } from "../../data/portalConfig";
import { createSoRule, listSoRules, updateSoRuleById } from "../../data/soRules";
import { conflictsForCandidate, findPriorityConflicts } from "../../services/soRuleConflicts";
import {
  DEFAULT_SO_COMMENTS_TEMPLATE,
  DEFAULT_SO_CUSTOMER_REFERENCE,
} from "../../services/soRulesEngine";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

const COMMENTS_KEY = "so_comments_template";
const CUSTREF_KEY = "so_customer_reference_template";
const TOKEN_HELP =
  "Tokens: {{child_codes}} {{school_names}} {{caseworker_name}} {{caseworker_email}} {{caseworker_mobile}} {{delivery}} {{dfv_service}} {{ref_no}} {{suffix}}";

// Hardcoded token reference for the "?" help popover. renderSoTemplate (soRulesEngine.ts)
// is the source of truth for what tokens exist — a NEW token needs a code change there, so
// this descriptive list is intentionally hardcoded and kept in sync by hand.
const TOKENS = [
  { t: "{{child_codes}}", d: "Child reference codes on this SO, space-separated (e.g. FY4 MY7)." },
  { t: "{{school_names}}", d: "School name(s) on this SO, comma-separated." },
  { t: "{{caseworker_name}}", d: "The requesting caseworker's name." },
  { t: "{{caseworker_email}}", d: "The caseworker's email address." },
  { t: "{{caseworker_mobile}}", d: "The caseworker's mobile number." },
  { t: "{{delivery}}", d: "The request's delivery address (one line)." },
  { t: "{{dfv_service}}", d: "The DFV Service (shelter) name." },
  { t: "{{ref_no}}", d: "The request reference number (e.g. 2026-00170)." },
  { t: "{{suffix}}", d: "The SO suffix / type (e.g. RMB, SCH, DEV)." },
];

// Editor for the SO comments block + customer-reference templates (portal_config).
// These render per Sales Order at Confirm & Send — see docs/so-rules-engine.md §2.
function SoTemplatesPanel({ notify }) {
  const [comments, setComments] = React.useState(DEFAULT_SO_COMMENTS_TEMPLATE);
  const [custRef, setCustRef] = React.useState(DEFAULT_SO_CUSTOMER_REFERENCE);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [showTokens, setShowTokens] = React.useState(false);

  React.useEffect(function () {
    let cancelled = false;
    getConfigValues([COMMENTS_KEY, CUSTREF_KEY]).then(function (rows) {
      if (cancelled) return;
      const m = {};
      for (const r of rows || []) m[r.key] = r.value;
      if (typeof m[COMMENTS_KEY] === "string") setComments(m[COMMENTS_KEY]);
      if (typeof m[CUSTREF_KEY] === "string") setCustRef(m[CUSTREF_KEY]);
      setLoading(false);
    });
    return function () {
      cancelled = true;
    };
  }, []);

  async function save() {
    setSaving(true);
    const e1 = await upsertConfig(COMMENTS_KEY, comments);
    const e2 = await upsertConfig(CUSTREF_KEY, custRef);
    setSaving(false);
    if (e1.error || e2.error) {
      notify("Save failed");
      return;
    }
    logAudit("so_templates_updated", {});
    notify("Templates saved ✓");
  }

  function resetDefaults() {
    setComments(DEFAULT_SO_COMMENTS_TEMPLATE);
    setCustRef(DEFAULT_SO_CUSTOMER_REFERENCE);
  }

  const inp =
    "w-full py-[6px] px-[9px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text bg-white font-mono";

  return (
    <details className="mb-5 border border-zephyr-border rounded-[10px] bg-white">
      <summary className="cursor-pointer select-none py-[10px] px-[14px] text-[14px] font-semibold text-zephyr-teal-dark">
        Sales Order comments &amp; customer reference (templates)
      </summary>
      <div className="p-4 pt-1">
        {loading ? (
          <div className="text-[13px] text-zephyr-muted py-3">Loading…</div>
        ) : (
          <>
            <p className="m-0 mb-3 text-[12px] text-zephyr-muted">{TOKEN_HELP}</p>
            <label className="block text-[12px] font-semibold text-zephyr-text mb-1">
              Comments block (goes on each SO)
            </label>
            <textarea
              aria-label="SO comments template"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={5}
              className={inp}
            />
            <label className="block text-[12px] font-semibold text-zephyr-text mt-4 mb-1">
              Customer Reference
            </label>
            <textarea
              aria-label="SO customer reference template"
              value={custRef}
              onChange={(e) => setCustRef(e.target.value)}
              rows={2}
              className={inp}
            />
            <div className="flex gap-3 items-center mt-4">
              <Btn onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save templates"}
              </Btn>
              <button
                type="button"
                onClick={resetDefaults}
                className="bg-transparent border border-zephyr-border rounded-[6px] py-[6px] px-[12px] text-[12px] cursor-pointer text-zephyr-text"
                style={{ fontFamily: "inherit" }}
              >
                Reset to defaults
              </button>
              <button
                type="button"
                aria-label="Show available tokens"
                aria-expanded={showTokens}
                onClick={() => setShowTokens((v) => !v)}
                className="w-6 h-6 rounded-full border border-zephyr-border text-zephyr-teal-dark text-[13px] font-bold cursor-pointer bg-white leading-none"
                style={{ fontFamily: "inherit" }}
              >
                ?
              </button>
            </div>
            {showTokens && (
              <div className="mt-3 border border-zephyr-border rounded-[8px] bg-[#F7F9F9] p-3">
                <div className="text-[12px] font-semibold text-zephyr-text mb-2">
                  Available tokens
                </div>
                <div
                  className="grid gap-y-1"
                  style={{ gridTemplateColumns: "auto 1fr", columnGap: 14 }}
                >
                  {TOKENS.map((tk) => (
                    <React.Fragment key={tk.t}>
                      <code className="text-[12px] text-zephyr-teal-dark font-mono whitespace-nowrap">
                        {tk.t}
                      </code>
                      <span className="text-[12px] text-zephyr-muted">{tk.d}</span>
                    </React.Fragment>
                  ))}
                </div>
                <p className="m-0 mt-2 text-[11px] text-zephyr-muted italic">
                  Per-SO tokens (child codes, school names) are scoped to that SO; the rest are
                  request-level. Adding a NEW token needs a code change — ask the dev team.
                </p>
                <p className="m-0 mt-2 text-[11px] text-zephyr-muted italic">
                  These set the SO-level <strong>comment</strong> and{" "}
                  <strong>customer reference</strong> only. The per-child SO{" "}
                  <strong>line comment</strong> is a separate per-rule setting on the{" "}
                  <strong>Edit rule</strong> screen (enabled when Line mode = per_child) — its own
                  tokens are{" "}
                  <code className="font-mono text-zephyr-teal-dark">{"{{child_code}}"}</code> and{" "}
                  <code className="font-mono text-zephyr-teal-dark">{"{{description}}"}</code>.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </details>
  );
}

const KIND_OPTS = ["device", "shoe", "fee", "uniform"];
const GROUP_OPTS = ["per_request", "per_child", "per_school", "per_provider", "per_supplier"];
const LINE_OPTS = ["per_child", "aggregate"];
const PAYEE_OPTS = ["", "school", "provider", "supplier", "dfv_service"];

const LIST_GRID = { gridTemplateColumns: "56px 1fr 150px 80px 130px 96px 150px" };

// Warns when two ACTIVE rules share a priority. The engine runs an ordered,
// consuming pass, so tied rules whose selectors can match the same item make the
// winner arbitrary — and with it the SO suffix, grouping and payee. A secondary
// sort by id now makes that choice at least repeatable, but only changing one of
// the priorities actually resolves it, which is what this banner is for.
// Non-overlapping ties (e.g. a device rule and a fee rule) are shown as
// information only — untidy, but neither can steal the other's items.
function PriorityConflictBanner({ rows }) {
  const conflicts = findPriorityConflicts(rows);
  if (conflicts.length === 0) return null;
  const harmful = conflicts.filter((c) => c.overlapping);
  const benign = conflicts.filter((c) => !c.overlapping);
  return (
    <div className="mb-4">
      {harmful.length > 0 && (
        <div
          className="rounded-[8px] px-4 py-3 mb-2 text-[13px]"
          style={{ background: "#FFEBEE", border: "1px solid #E57373", color: "#B71C1C" }}
        >
          <div className="font-bold mb-1">
            ⚠️ {harmful.length === 1 ? "A priority is" : `${harmful.length} priorities are`} shared
            by rules that can claim the same item
          </div>
          {harmful.map((c) => (
            <div key={c.priority}>
              Priority <strong>{c.priority}</strong>: {c.labels.join(" · ")}
            </div>
          ))}
          <div className="mt-2">
            Which rule wins is arbitrary. Give one of them a different priority to decide it
            deliberately.
          </div>
        </div>
      )}
      {benign.length > 0 && (
        <div
          className="rounded-[8px] px-4 py-2 text-[12px]"
          style={{ background: "#FFF7E6", border: "1px solid #FFCC80", color: "#8a5a00" }}
        >
          <strong>Shared priorities (harmless):</strong>{" "}
          {benign.map((c) => `${c.priority} — ${c.labels.join(", ")}`).join("; ")}. These rules
          match different kinds of item, so neither can take the other&rsquo;s.
        </div>
      )}
    </div>
  );
}

function emptyRule() {
  return {
    priority: "",
    label: "",
    match_kind: "fee",
    match_fee_types: "",
    match_state: "",
    match_supplier_kind: "",
    match_supplier_suffix: "",
    match_payment_method: "",
    suffix: "",
    group_by: "per_request",
    line_mode: "aggregate",
    payee_type: "",
    customer_reference: "",
    line_comment_template: "",
    requires_picker: false,
    active: true,
  };
}

function ruleToForm(r) {
  return {
    priority: r.priority ?? "",
    label: r.label ?? "",
    match_kind: r.match_kind ?? "fee",
    match_fee_types: Array.isArray(r.match_fee_types) ? r.match_fee_types.join(", ") : "",
    match_state: r.match_state ?? "",
    match_supplier_kind: r.match_supplier_kind ?? "",
    match_supplier_suffix: r.match_supplier_suffix ?? "",
    match_payment_method: r.match_payment_method ?? "",
    suffix: r.suffix ?? "",
    group_by: r.group_by ?? "per_request",
    line_mode: r.line_mode ?? "aggregate",
    payee_type: r.payee_type ?? "",
    customer_reference: r.customer_reference ?? "",
    line_comment_template: r.line_comment_template ?? "",
    requires_picker: !!r.requires_picker,
    active: !!r.active,
  };
}

// A short human summary of a rule's selector for the list.
function matchSummary(r) {
  const parts = [r.match_kind];
  if (r.match_fee_types?.length) parts.push(`(${r.match_fee_types.join(", ")})`);
  if (r.match_state) parts.push(`state=${r.match_state}`);
  if (r.match_supplier_kind) parts.push(`kind=${r.match_supplier_kind}`);
  if (r.match_supplier_suffix) parts.push(`suffix=${r.match_supplier_suffix}`);
  if (r.match_payment_method) parts.push(`pay=${r.match_payment_method}`);
  return parts.join(" ");
}

function RuleModal({ mode, row, allRows, onClose, onSaved }) {
  const [form, setForm] = React.useState(() =>
    mode === "edit" && row ? ruleToForm(row) : emptyRule()
  );
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Live conflict check against the other ACTIVE rules, so a clash is visible while
  // you type the priority rather than only after saving. Not a hard block — a shared
  // priority is legal and sometimes harmless; the warning is what makes the harmful
  // case (overlapping selectors) obvious. Finding B16 item 3.
  const candidate = {
    id: row?.id ?? null,
    label: form.label,
    priority: Number.parseInt(form.priority, 10),
    active: !!form.active,
    match_kind: form.match_kind,
    match_fee_types: form.match_fee_types
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    match_state: form.match_state || null,
    match_supplier_kind: form.match_supplier_kind || null,
    match_supplier_suffix: form.match_supplier_suffix || null,
    match_payment_method: form.match_payment_method || null,
  };
  const clash = Number.isFinite(candidate.priority)
    ? conflictsForCandidate(candidate, allRows)
    : { overlapping: [], sharing: [] };

  async function save() {
    const label = form.label.trim();
    const priority = Number.parseInt(form.priority, 10);
    if (!label) {
      setErr("Label is required.");
      return;
    }
    if (!Number.isFinite(priority)) {
      setErr("Priority must be a number.");
      return;
    }
    const t = (v) => (v || "").trim() || null;
    const feeTypes = form.match_fee_types
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const body = {
      priority,
      label,
      match_kind: form.match_kind,
      match_fee_types: feeTypes.length ? feeTypes : null,
      match_state: t(form.match_state),
      match_supplier_kind: t(form.match_supplier_kind),
      match_supplier_suffix: t(form.match_supplier_suffix),
      match_payment_method: t(form.match_payment_method),
      suffix: t(form.suffix),
      group_by: form.group_by,
      line_mode: form.line_mode,
      payee_type: t(form.payee_type),
      customer_reference: t(form.customer_reference),
      line_comment_template: t(form.line_comment_template),
      requires_picker: !!form.requires_picker,
      active: !!form.active,
    };
    setSaving(true);
    setErr("");
    const { error } =
      mode === "edit" ? await updateSoRuleById(row.id, body) : await createSoRule(body);
    setSaving(false);
    if (error) {
      setErr(
        /duplicate|unique/i.test(error.message) ? `Label "${label}" already exists.` : error.message
      );
      return;
    }
    logAudit(mode === "edit" ? "so_rule_updated" : "so_rule_created", {
      id: row?.id,
      label,
      priority,
    });
    onSaved(mode);
  }

  const lab = "text-[12px] text-zephyr-muted text-right pr-3 pt-2";
  const inp =
    "py-[6px] px-[9px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text bg-white";

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      contentStyle={{ maxWidth: 720, background: "#fff", borderRadius: 12, padding: "16px 20px" }}
    >
      <div className="p-1">
        <div className="flex justify-between items-center mb-3">
          <h2 className="m-0 text-[20px] text-zephyr-teal-dark">
            {mode === "edit" ? "Edit rule" : "New rule"}
          </h2>
          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="bg-transparent border border-zephyr-border rounded-[6px] py-[6px] px-[14px] text-[13px] cursor-pointer text-zephyr-text"
              style={{ fontFamily: "inherit" }}
            >
              Cancel
            </button>
            <Btn onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Btn>
          </div>
        </div>
        {err && (
          <div className="mb-3 text-[13px] text-[#C62828] bg-[#FFF3F3] border border-[#E0A0A0] rounded-[6px] py-2 px-3">
            {err}
          </div>
        )}
        <div className="grid items-start gap-y-2" style={{ gridTemplateColumns: "150px 1fr" }}>
          <label className={lab}>Priority</label>
          <div>
            <input
              aria-label="Priority"
              value={form.priority}
              onChange={(e) => set("priority", e.target.value)}
              className={`${inp} w-[100px]`}
            />
            {clash.overlapping.length > 0 ? (
              <div className="text-[12px] mt-1 font-semibold" style={{ color: "#C62828" }}>
                ⚠️ Priority {candidate.priority} is already used by{" "}
                {clash.overlapping.map((r) => r.label || `#${r.id}`).join(", ")}, which can claim
                the same items. Which rule wins would be arbitrary.
              </div>
            ) : clash.sharing.length > 0 ? (
              <div className="text-[12px] mt-1" style={{ color: "#8a5a00" }}>
                Priority {candidate.priority} is also used by{" "}
                {clash.sharing.map((r) => r.label || `#${r.id}`).join(", ")} — harmless, they match
                different items.
              </div>
            ) : null}
          </div>

          <label className={lab}>Label</label>
          <input
            aria-label="Label"
            value={form.label}
            onChange={(e) => set("label", e.target.value)}
            className={`${inp} w-full`}
          />

          <label className={lab}>Match kind</label>
          <select
            aria-label="Match kind"
            value={form.match_kind}
            onChange={(e) => set("match_kind", e.target.value)}
            className={`${inp} w-[180px]`}
          >
            {KIND_OPTS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>

          <label className={lab}>Fee types</label>
          <input
            aria-label="Fee types"
            value={form.match_fee_types}
            onChange={(e) => set("match_fee_types", e.target.value)}
            placeholder="comma-separated (fee rules)"
            className={`${inp} w-full`}
          />

          <label className={lab}>State gate</label>
          <input
            aria-label="State gate"
            value={form.match_state}
            onChange={(e) => set("match_state", e.target.value)}
            placeholder="e.g. QLD"
            className={`${inp} w-[120px]`}
          />

          <label className={lab}>Supplier kind</label>
          <input
            aria-label="Supplier kind"
            value={form.match_supplier_kind}
            onChange={(e) => set("match_supplier_kind", e.target.value)}
            placeholder="e.g. school_locker"
            className={`${inp} w-[220px]`}
          />

          <label className={lab}>Supplier suffix</label>
          <input
            aria-label="Supplier suffix"
            value={form.match_supplier_suffix}
            onChange={(e) => set("match_supplier_suffix", e.target.value)}
            placeholder="e.g. HOK"
            className={`${inp} w-[120px]`}
          />

          <label className={lab}>Payment method</label>
          <input
            aria-label="Payment method"
            value={form.match_payment_method}
            onChange={(e) => set("match_payment_method", e.target.value)}
            placeholder="e.g. shelter_reimbursed"
            className={`${inp} w-[220px]`}
          />

          <label className={lab}>Suffix (SO)</label>
          <input
            aria-label="Suffix"
            value={form.suffix}
            onChange={(e) => set("suffix", e.target.value)}
            placeholder="blank = supplier's so_suffix"
            className={`${inp} w-[220px]`}
          />

          <label className={lab}>Group by</label>
          <select
            aria-label="Group by"
            value={form.group_by}
            onChange={(e) => set("group_by", e.target.value)}
            className={`${inp} w-[180px]`}
          >
            {GROUP_OPTS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>

          <label className={lab}>Line mode</label>
          <select
            aria-label="Line mode"
            value={form.line_mode}
            onChange={(e) => set("line_mode", e.target.value)}
            className={`${inp} w-[180px]`}
          >
            {LINE_OPTS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>

          <label className={lab}>Payee type</label>
          <select
            aria-label="Payee type"
            value={form.payee_type}
            onChange={(e) => set("payee_type", e.target.value)}
            className={`${inp} w-[180px]`}
          >
            {PAYEE_OPTS.map((p) => (
              <option key={p} value={p}>
                {p || "—"}
              </option>
            ))}
          </select>

          <label className={lab}>Customer Ref</label>
          <input
            aria-label="Customer reference"
            value={form.customer_reference}
            onChange={(e) => set("customer_reference", e.target.value)}
            placeholder="e.g. {{school_names}} Fees — blank = global default"
            className={`${inp} w-full font-mono`}
          />

          <label className={lab}>Line comment</label>
          <div>
            <input
              aria-label="Line comment template"
              value={form.line_comment_template}
              onChange={(e) => set("line_comment_template", e.target.value)}
              disabled={form.line_mode !== "per_child"}
              placeholder="{{child_code}} — {{description}}"
              className={`${inp} w-full font-mono ${
                form.line_mode !== "per_child" ? "opacity-50 cursor-not-allowed" : ""
              }`}
            />
            <div className="text-[11px] text-zephyr-muted mt-1">
              {form.line_mode === "per_child"
                ? "Per-child SO line comment. Tokens: {{child_code}} (this line's child ref) · {{description}} (the fee description — blank on non-fee lines). Blank = just the child code. Max 1024 characters."
                : "Line-comment templating applies only when Line mode is per_child (aggregate lines list the child codes)."}
            </div>
          </div>

          {/* db-114. Was DB-only, so standing up a new picker rule needed hand-written
              SQL even though every other field on the rule was editable here.
              Finding B16 item 2, 18 Aug 2026. */}
          <label className={lab}>Picker</label>
          <div className="pt-1">
            <label className="flex items-center gap-2 text-[13px] text-zephyr-text">
              <input
                type="checkbox"
                checked={form.requires_picker}
                onChange={(e) => set("requires_picker", e.target.checked)}
              />
              Requires a picker
            </label>
            <div className="text-[11px] text-zephyr-muted mt-1">
              The Sales Order is parked on <strong>hold</strong> and a picker task is raised per SO.
              The order only reaches Unleashed once the picker records the actual cost and order
              number.
            </div>
          </div>

          <label className={lab}>Active</label>
          <label className="flex items-center gap-2 text-[13px] text-zephyr-text pt-1">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => set("active", e.target.checked)}
            />
            Active (engine applies it)
          </label>
        </div>
      </div>
    </ModalBackdrop>
  );
}

export function SORulesAdmin() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [savedMsg, setSavedMsg] = React.useState("");
  const [tick, setTick] = React.useState(0);
  const [modal, setModal] = React.useState(null);

  React.useEffect(
    function () {
      let cancelled = false;
      setLoading(true);
      listSoRules().then(function (data) {
        if (cancelled) return;
        if (data) setRows(data);
        setLoading(false);
      });
      return function () {
        cancelled = true;
      };
    },
    [tick]
  );

  const refresh = () => setTick((n) => n + 1);
  function showSaved(m) {
    setSavedMsg(m || "Saved ✓");
    setTimeout(() => setSavedMsg(""), 3000);
  }
  function onSaved(mode) {
    setModal(null);
    refresh();
    showSaved(mode === "create" ? "Rule created ✓" : "Saved ✓");
  }
  async function toggleActive(r) {
    const { error } = await updateSoRuleById(r.id, { active: !r.active });
    if (error) {
      showSaved("Update failed");
      return;
    }
    logAudit(r.active ? "so_rule_deactivated" : "so_rule_activated", { id: r.id, label: r.label });
    refresh();
    showSaved(r.active ? "Rule deactivated ✓" : "Rule activated ✓");
  }

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div className="mb-1 text-[12px] text-zephyr-muted font-semibold">Unleashed</div>
      <div className="flex justify-between items-center mb-3">
        <h1 className="m-0 text-[22px] text-zephyr-text">SO Rules</h1>
        {savedMsg && <span className="text-[13px] text-[#2E7D32] font-semibold">{savedMsg}</span>}
      </div>
      <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
        The ordered pipeline the Sales Order engine runs at Confirm &amp; Send. Each rule claims a
        slice of the approved items and groups them into SO(s); rules sharing a suffix + grouping
        merge into one SO.{" "}
        <span className="text-[17px] font-bold underline">
          Anything unclaimed is swept into a <code>ZZZ</code> safety-net SO with an alert.
        </span>
      </p>

      <SoTemplatesPanel notify={showSaved} />

      <PriorityConflictBanner rows={rows} />

      <div className="flex gap-4 items-center mb-4">
        <span className="text-[12px] text-zephyr-muted">{rows.length} rules</span>
        <Btn onClick={() => setModal({ mode: "create", row: null })} style={{ marginLeft: "auto" }}>
          + New rule
        </Btn>
      </div>

      {loading ? (
        <div className="text-center text-zephyr-muted p-10">Loading…</div>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
          <div
            className="grid gap-0 bg-zephyr-teal-dark text-white py-[7px] px-[13px] text-[11px] font-bold uppercase tracking-[0.5px]"
            style={LIST_GRID}
          >
            <div>Pri</div>
            <div>Match</div>
            <div>Label</div>
            <div>Suffix</div>
            <div>Group</div>
            <div>Line</div>
            <div>Actions</div>
          </div>
          {rows.map(function (r, idx) {
            return (
              <div
                key={r.id}
                className="grid gap-0 py-[7px] px-[13px] items-center"
                style={{
                  ...LIST_GRID,
                  borderBottom: idx < rows.length - 1 ? "1px solid #CFE0DE" : "none",
                  background: r.active ? (idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9") : "#FFF3F3",
                }}
              >
                <div className="text-[13px] font-semibold text-zephyr-text">{r.priority}</div>
                <div className="text-[12px] text-zephyr-muted overflow-hidden text-ellipsis whitespace-nowrap">
                  {matchSummary(r)}
                </div>
                <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => setModal({ mode: "edit", row: r })}
                    className="bg-transparent border-0 p-0 font-semibold text-[13px] underline cursor-pointer text-left"
                    style={{ color: r.active ? "#00695C" : "#607D79", fontFamily: "inherit" }}
                  >
                    {r.label}
                  </button>
                  {!r.active && (
                    <span className="ml-[6px] text-[10px] font-bold text-white bg-[#C62828] rounded-[3px] py-[1px] px-[6px]">
                      OFF
                    </span>
                  )}
                </div>
                <div className="text-[13px] font-semibold text-[#00897B]">
                  {r.suffix || "→supplier"}
                </div>
                <div className="text-[12px] text-zephyr-muted">{r.group_by}</div>
                <div className="text-[12px] text-zephyr-muted">{r.line_mode}</div>
                <div className="flex gap-[6px]">
                  <button
                    type="button"
                    onClick={() => setModal({ mode: "edit", row: r })}
                    className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer border border-[#CFE0DE] text-[#1A2E2B]"
                    style={{ fontFamily: "inherit" }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleActive(r)}
                    className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer border"
                    style={{
                      fontFamily: "inherit",
                      borderColor: r.active ? "#E0A0A0" : "#2E7D32",
                      color: r.active ? "#C62828" : "#2E7D32",
                    }}
                  >
                    {r.active ? "Deactivate" : "Activate"}
                  </button>
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="p-10 text-center text-zephyr-muted text-[13px]">No rules yet.</div>
          )}
        </div>
      )}

      {modal && (
        <RuleModal
          mode={modal.mode}
          row={modal.row}
          allRows={rows}
          onClose={() => setModal(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
